<template>
  <v-form ref="formRef" autocomplete="off">
    <v-container fluid class="presupuesto-pedido-form-container pa-2">
      <div
        class="order-header-panel mb-3"
        :class="{
          'order-header-panel--fixed-document': tipoDocumentoFijo,
          'order-header-panel--without-lista': !showListaPreciosSelector,
        }"
      >
        <div class="order-header-fields">
          <div v-if="!tipoDocumentoFijo" class="order-header-field order-header-field--tipo">
            <v-radio-group v-model="form.tipoDocumento" hide-details row class="mt-0 pt-0 tipo-doc-radios">
              <v-radio
                :label="$t('producto.modals.presupuestoPedido.tipoPresupuesto')"
                value="presupuesto"
                color="primary"
              />
              <v-radio
                :label="$t('producto.modals.presupuestoPedido.tipoPedido')"
                value="pedido"
                color="primary"
              />
            </v-radio-group>
          </div>
          <div class="order-header-field order-header-field--cliente">
            <v-autocomplete
              v-model="form.clienteId"
              :items="clientes"
              item-text="razonSocial"
              item-value="id"
              outlined
              dense
              :rules="clienteRules"
              :loading="loadingClientes"
              :search-input.sync="searchCliente"
              hide-no-data
              hide-details="auto"
              clearable
              no-filter
              placeholder="Buscar cliente…"
              label="Cliente"
              color="primary"
              prepend-inner-icon="mdi-account"
            />
          </div>
          <div class="order-header-field order-header-field--vendedor">
            <v-autocomplete
              v-model="form.vendedorId"
              :items="vendedores"
              :item-text="v => v.nombre + ' ' + v.apellido"
              item-value="id"
              outlined
              dense
              :loading="loadingVendedores"
              hide-details="auto"
              clearable
              placeholder="Vendedor (opcional)"
              label="Vendedor"
              color="primary"
              prepend-inner-icon="mdi-account-tie"
            />
          </div>
          <div class="order-header-field order-header-field--descuento">
            <v-text-field
              v-model.number="descuento"
              type="number"
              outlined
              dense
              hide-details
              min="0"
              max="100"
              step="0.01"
              label="Descuento (%)"
              :rules="descuentoRules"
              prepend-inner-icon="mdi-tag-outline"
            />
          </div>
          <div v-if="showListaPreciosSelector" class="order-header-field order-header-field--lista">
            <v-select
              v-model="form.listaPreciosVentaId"
              :items="listasPrecios"
              item-text="nombre"
              item-value="id"
              outlined
              dense
              hide-details
              clearable
              label="Lista de precios"
              placeholder="Lista default"
              prepend-inner-icon="mdi-format-list-numbered"
              @change="onListaPreciosChange"
            />
          </div>
          <div
            class="order-header-field order-header-field--recargo"
            :class="{ 'order-header-field--recargo-manual': recargoSeleccionado === 'manual' }"
          >
            <v-select
              v-model="recargoSeleccionado"
              :items="recargoOpciones"
              item-text="label"
              item-value="value"
              outlined
              dense
              hide-details
              clearable
              label="Recargo"
              placeholder="Sin recargo"
              prepend-inner-icon="mdi-plus-circle-outline"
              @change="onRecargoSeleccionadoChange"
            />
            <v-text-field
              v-if="recargoSeleccionado === 'manual'"
              v-model.number="recargoManual"
              type="number"
              outlined
              dense
              hide-details
              min="0"
              max="100"
              step="0.01"
              placeholder="% manual"
              :rules="recargoRules"
            />
          </div>
        </div>
      </div>
      <v-row no-gutters>
        <movimiento-compra-list
          :items="itemsCarritoParaTabla"
          v-model="form"
          ref="compraListRef"
          :auto-open-search-dialog="false"
          :limit-cantidad-by-stock="form.tipoDocumento === 'pedido'"
          :allow-add-zero-stock="form.tipoDocumento === 'presupuesto'"
          :precio-editable="true"
          :cart-table-height="cartTableHeight"
          @addProductToCart="addProductToCart"
          @updateCantidad="onUpdateCantidad"
          @updatePrecio="onUpdatePrecio"
          @resetForm="resetForm"
          @delete="deleteItem"
          @closeDialog="tableDialogClosed"
          @error="$emit('error', $event)"
        />
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import MovimientoCompraList from "../../../movimiento/pages/MovimientoManagementPage/MovimientoList/MovimientoCompraList.vue";
import ClienteProvider from "../../../cliente/providers/ClienteProvider";
import RecargoProvider from "../../../recargo/providers/RecargoProvider";
import OfertaProvider from "../../../oferta/providers/OfertaProvider";
import { aplicarOfertasACarritoPreview } from "../../../oferta/utils/ofertaCalculo";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";
import VendedorProvider from "../../providers/VendedorProvider";
import ListaPreciosVentaProvider from "../../../lista-precios-venta/providers/ListaPreciosVentaProvider";

export default {
  name: "PresupuestoPedidoForm",
  components: { MovimientoCompraList },
  props: {
    initialItems: { type: Array, default: () => [] },
    initialClienteId: { type: String, default: null },
    depositoId: { type: String, default: null },
    tipoDocumentoFijo: { type: String, default: null },
    initialDescuento: { type: Number, default: 0 },
    initialRecargo: { type: Number, default: 0 },
    initialListaPreciosVentaId: { type: String, default: null },
    initialVendedorId: { type: String, default: null },
    cartTableHeight: { type: String, default: 'calc(100vh - 390px)' },
  },
  data() {
    return {
      form: {
        idProducto: null,
        clienteId: null,
        vendedorId: null,
        tipoDocumento: "presupuesto",
        listaPreciosVentaId: null,
      },
      itemsCarrito: [],
      loadingClientes: false,
      clientes: [],
      searchCliente: null,
      vendedores: [],
      loadingVendedores: false,
      listasPrecios: [],
      descuento: 0,
      recargoSeleccionado: null,
      recargoManual: 0,
      recargosLista: [],
      ofertasAplicables: [],
      loadingOfertas: false,
      isUpdatingPrices: false,
      pendingPrecioRecalc: false,
      loadingPreciosLista: false,
      descuentoRules: [
        (v) =>
          (parseFloat(v) >= 0 && parseFloat(v) <= 100) ||
          "El descuento debe ser entre 0% y 100%",
      ],
      recargoRules: [
        (v) =>
          (parseFloat(v) >= 0 && parseFloat(v) <= 100) ||
          "El recargo debe ser entre 0% y 100%",
      ],
      clienteRules: [
        (v) => !!v || (this.$t("producto.modals.presupuestoPedido.errorCliente") || "El cliente es requerido"),
      ],
    };
  },
  computed: {
    recargoOpciones() {
      const manual = { label: "Valor manual", value: "manual" };
      const deLista = (this.recargosLista || []).map((r) => ({
        label: `${r.nombre} (${r.recargo}%)`,
        value: r.id,
      }));
      return [manual, ...deLista];
    },
    recargoNum() {
      if (this.recargoSeleccionado === "manual") {
        const v = this.recargoManual;
        return v === "" || v == null ? 0 : parseFloat(v);
      }
      if (this.recargoSeleccionado) {
        const r = (this.recargosLista || []).find(
          (x) => x.id === this.recargoSeleccionado
        );
        return r ? r.recargo || 0 : 0;
      }
      return 0;
    },
    subtotal() {
      let total = 0;
      for (const item of this.itemsCarrito || []) {
        const p = item.producto || item.product;
        const precio = (p && p.precio != null) ? Number(p.precio) : (p && p.precioVenta != null ? Number(p.precioVenta) : 0);
        const cantidad = Math.max(0, Number(item.cantidad) || 0);
        const bruto = precio * cantidad;
        const descOferta = (item.ofertaAplicada && item.ofertaAplicada.descuento) || 0;
        total += bruto - descOferta;
      }
      return total;
    },
    itemsCarritoParaTabla() {
      return (this.itemsCarrito || []).map((item) => {
        const p = item.producto || item.product;
        const precioBase = p && (p.precio != null) ? Number(p.precio) : (p && p.precioVenta != null ? Number(p.precioVenta) : 0);
        const cantidad = Number(item.cantidad) || 0;
        let precioFinal = precioBase;
        if (item.ofertaAplicada && item.ofertaAplicada.descuento > 0 && cantidad > 0) {
          const descuentoPorUnidad = item.ofertaAplicada.descuento / cantidad;
          precioFinal = precioBase - descuentoPorUnidad;
        }
        const marca = (p && p.marca && p.marca.nombre) ? p.marca.nombre : (p && p.marca) || "";
        const modelo = (p && p.modelo && p.modelo.nombre) ? p.modelo.nombre : (p && p.modelo) || "";
        const tamanio = (p && p.tamanio && p.tamanio.tamanio) ? p.tamanio.tamanio : (p && p.tamanio) || null;
        const nombre = (p && p.nombre) || [marca, modelo, tamanio].filter(Boolean).join(" · ") || "-";
        return {
          id: (p && (p.id || p._id)) || null,
          stock: (p && p.stock) != null ? p.stock : 0,
          nombre,
          precio: precioFinal,
          precioBase,
          cantidad,
          ofertaAplicada: item.ofertaAplicada || null,
          precioManual: !!item.precioManual,
        };
      });
    },
    precioTotal() {
      const desc = Number(this.descuento) || 0;
      const rec = this.recargoNum;
      const total =
        this.subtotal * (1 - desc / 100) * (1 + rec / 100);
      return total.toFixed(2);
    },
    showListaPreciosSelector() {
      return this.listasPrecios.length > 1;
    },
  },
  watch: {
    initialItems: {
      immediate: true,
      async handler(items) {
        if (items && items.length > 0) {
          this.itemsCarrito = items.map((b) => ({
            producto: b.product || b.producto,
            cantidad: Math.max(1, Number(b.cantidad) || 1),
            ofertaAplicada: b.ofertaAplicada || null,
            precioManual: !!b.precioManual,
            precioCatalogo: b.precioCatalogo != null ? Number(b.precioCatalogo) : null,
          }));
          await this.actualizarPreciosCarritoPorLista();
          this.$nextTick(() => this.updatePrecioTotal());
        }
      },
    },
    initialClienteId: {
      immediate: true,
      handler(id) {
        if (id) this.form.clienteId = id;
      },
    },
    initialVendedorId: {
      immediate: true,
      handler(id) {
        if (id) this.form.vendedorId = id;
      },
    },
    initialDescuento: {
      immediate: true,
      handler(val) {
        const v = Number(val) || 0;
        this.descuento = v;
      },
    },
    initialRecargo: {
      immediate: true,
      handler(val) {
        const v = Number(val) || 0;
        // Si hay recargo inicial, lo cargamos como valor manual para que sea visible
        if (v > 0) {
          this.recargoSeleccionado = "manual";
          this.recargoManual = v;
        } else {
          this.recargoSeleccionado = null;
          this.recargoManual = 0;
        }
      },
    },
    initialListaPreciosVentaId: {
      immediate: true,
      handler(id) {
        if (id) this.form.listaPreciosVentaId = id;
      },
    },
    itemsCarrito: {
      handler() {
        if (!this.isUpdatingPrices) {
          this.updatePrecioTotal();
        }
      },
      deep: true,
    },
    "form.tipoDocumento"(nuevo) {
      this.$emit("update:tipoDocumento", nuevo);
      if (nuevo === "pedido") {
        this.ajustarCarritoParaPedido();
      }
    },
    depositoId(val) {
      if (val && this.itemsCarrito && this.itemsCarrito.length > 0) {
        this.updatePrecioTotal();
      }
    },
    precioTotal: {
      immediate: true,
      handler(v) {
        this.$emit("total-change", {
          total: v,
          subtotal: this.subtotal.toFixed(2),
          descuento: Number(this.descuento) || 0,
          recargo: this.recargoNum,
        });
      },
    },
    searchCliente(val) {
      const q = val && String(val).trim();
      if (q && q.length >= 2) {
        this.buscarClientes(q);
      } else if (!q) {
        this.loadClientes();
      }
    },
    "form.clienteId"(newId) {
      // Si el cliente tiene una lista de precios default y el usuario aún no
      // forzó otra, auto-seleccionar la del cliente.
      if (!newId) return;
      const cliente = this.clientes.find((c) => String(c.id) === String(newId));
      const listaCliente = cliente && cliente.listaPreciosVentaDefaultId;
      if (listaCliente && this.form.listaPreciosVentaId !== listaCliente) {
        this.form.listaPreciosVentaId = listaCliente;
        this.onListaPreciosChange();
      }
    },
  },
  created() {
    if (this.tipoDocumentoFijo === "presupuesto" || this.tipoDocumentoFijo === "pedido") {
      this.form.tipoDocumento = this.tipoDocumentoFijo;
    }
    if (this.initialClienteId) {
      this.form.clienteId = this.initialClienteId;
    }
    this.loadClientes();
    this.loadRecargos();
    this.loadVendedores();
    this.loadListasPrecios();
    if (this.initialItems && this.initialItems.length > 0) {
      this.itemsCarrito = this.initialItems.map((b) => ({
        producto: b.product || b.producto,
        cantidad: Math.max(1, Number(b.cantidad) || 1),
        ofertaAplicada: b.ofertaAplicada || null,
        precioManual: !!b.precioManual,
        precioCatalogo: b.precioCatalogo != null ? Number(b.precioCatalogo) : null,
      }));
    }
    this.$nextTick(() => this.updatePrecioTotal());
  },
  methods: {
    async loadListasPrecios() {
      try {
        const r = await ListaPreciosVentaProvider.list();
        this.listasPrecios = (r.data && r.data.listaPreciosVentaList) || [];
        if (this.listasPrecios.length === 1) {
          this.form.listaPreciosVentaId = this.listasPrecios[0].id;
        } else if (!this.form.listaPreciosVentaId) {
          if (this.initialListaPreciosVentaId) {
            this.form.listaPreciosVentaId = this.initialListaPreciosVentaId;
          } else {
            const def = this.listasPrecios.find((l) => l.isDefault);
            const selected = def || this.listasPrecios[0];
            if (selected) this.form.listaPreciosVentaId = selected.id;
          }
        }
        if (this.itemsCarrito.length > 0) {
          await this.actualizarPreciosCarritoPorLista();
        }
      } catch (e) {
        this.listasPrecios = [];
      }
    },
    productoIdStr(producto) {
      if (!producto) return null;
      const id = producto.id != null ? producto.id : producto._id;
      return id != null ? String(id) : null;
    },
    async actualizarPreciosCarritoPorLista() {
      const productoIds = (this.itemsCarrito || [])
        .map((item) => this.productoIdStr(item.producto || item.product))
        .filter(Boolean);
      if (!productoIds.length) return;
      this.loadingPreciosLista = true;
      try {
        const r = await ListaPreciosVentaProvider.productosPreciosVenta(
          productoIds,
          this.form.listaPreciosVentaId || null
        );
        const rows = (r.data && r.data.productosPreciosVenta) || [];
        const precioPorId = new Map(
          rows.map((row) => [String(row.productoId), Number(row.precio) || 0])
        );
        for (const item of this.itemsCarrito) {
          const p = item.producto || item.product;
          const pid = this.productoIdStr(p);
          if (!pid || !p) continue;
          const precio = precioPorId.get(pid);
          if (precio != null) {
            this.$set(item, "precioCatalogo", precio);
            if (item.precioManual) continue;
            this.$set(p, "precio", precio);
            this.$set(p, "precioVenta", precio);
          }
        }
      } catch (e) {
        // Si falla la resolución, se mantienen los precios actuales
      } finally {
        this.loadingPreciosLista = false;
      }
    },
    async onListaPreciosChange() {
      this.ofertasAplicables = [];
      await this.actualizarPreciosCarritoPorLista();
      this.$nextTick(() => this.updatePrecioTotal());
    },
    async loadVendedores() {
      this.loadingVendedores = true;
      try {
        const r = await VendedorProvider.fetchVendedores();
        this.vendedores = (r.data && r.data.vendedorFetch) || [];
      } catch (e) {
        this.vendedores = [];
      } finally {
        this.loadingVendedores = false;
      }
    },
    onRecargoSeleccionadoChange() {
      if (this.recargoSeleccionado !== "manual") {
        const r = (this.recargosLista || []).find(
          (x) => x.id === this.recargoSeleccionado
        );
        this.recargoManual = r ? r.recargo || 0 : 0;
      }
    },
    async loadRecargos() {
      try {
        const r = await RecargoProvider.fetchRecargos();
        const list = (r.data && r.data.recargoFetch) || [];
        this.recargosLista = Array.isArray(list) ? list : [];
      } catch (e) {
        this.recargosLista = [];
      }
    },
    companyIdForCliente() {
      return this.$store.state.user && this.$store.state.user.companyId;
    },
    async loadClientes() {
      this.loadingClientes = true;
      try {
        const cid = this.companyIdForCliente();
        const filters = cid ? { companyId: cid } : {};
        const r = await ClienteProvider.paginateClientes(1, 80, "", null, false, filters);
        this.clientes = (r.data && r.data.clientePaginate && r.data.clientePaginate.items) || [];
      } finally {
        this.loadingClientes = false;
      }
    },
    async buscarClientes(search) {
      const cid = this.companyIdForCliente();
      if (!cid) return;
      this.loadingClientes = true;
      try {
        const r = await ClienteProvider.paginateClientes(1, 40, search, null, false, { companyId: cid });
        this.clientes = (r.data && r.data.clientePaginate && r.data.clientePaginate.items) || [];
      } finally {
        this.loadingClientes = false;
      }
    },
    getStockRow(row) {
      const p = row.producto || row.product;
      if (!p) return 0;
      return p.stock != null ? Number(p.stock) : 0;
    },
    ajustarCarritoParaPedido() {
      this.itemsCarrito.forEach((row) => {
        const stock = this.getStockRow(row);
        if (stock < 1) {
          row.cantidad = 0;
        } else {
          row.cantidad = Math.min(Number(row.cantidad) || 1, stock);
        }
      });
    },
    onUpdateCantidad({ item, newCantidad }) {
      const productId = item.id || (item.producto && (item.producto.id || item.producto._id));
      const row = this.itemsCarrito.find(
        (it) => String((it.producto || it.product) && ((it.producto || it.product).id || (it.producto || it.product)._id)) === String(productId)
      );
      if (row) {
        row.cantidad = Math.max(1, Number(newCantidad) || 1);
      }
    },
    onUpdatePrecio({ item, newPrecio }) {
      const productId = item.id || (item.producto && (item.producto.id || item.producto._id));
      const row = this.itemsCarrito.find(
        (it) => String((it.producto || it.product) && ((it.producto || it.product).id || (it.producto || it.product)._id)) === String(productId)
      );
      if (!row) return;
      const p = row.producto || row.product;
      if (!p) return;
      const precio = Math.round((Number(newPrecio) || 0) * 100) / 100;
      if (row.precioCatalogo == null) {
        const actual = p.precio != null ? Number(p.precio) : p.precioVenta != null ? Number(p.precioVenta) : null;
        if (actual != null && Number.isFinite(actual)) {
          this.$set(row, "precioCatalogo", Math.round(actual * 100) / 100);
        }
      }
      this.$set(p, "precio", precio);
      this.$set(p, "precioVenta", precio);
      const catalogo = row.precioCatalogo != null ? Number(row.precioCatalogo) : precio;
      this.$set(row, "precioManual", Math.abs(precio - catalogo) > 0.005);
    },
    async addProductToCart(item) {
      const existing = this.itemsCarrito.find(
        (it) => (it.producto && it.producto.id) === (item.producto && item.producto.id)
      );
      const cantidadNueva = item.cantidad || 1;
      const stockDisponible = (item.producto && item.producto.stock) ?? 0;
      const cantidadActual = existing ? existing.cantidad : 0;
      const isPedido = this.form.tipoDocumento === "pedido";
      if (isPedido && cantidadActual + cantidadNueva > stockDisponible) return;
      if (existing) {
        existing.cantidad += cantidadNueva;
      } else {
        this.itemsCarrito.push({ producto: item.producto, cantidad: cantidadNueva });
        await this.actualizarPreciosCarritoPorLista();
      }
    },
    deleteItem(item) {
      const index = this.itemsCarrito.findIndex(
        (it) => (it.producto && (it.producto.id || it.producto._id)) === item.id
      );
      if (index > -1) this.itemsCarrito.splice(index, 1);
    },
    async updatePrecioTotal() {
      if (this.isUpdatingPrices) {
        this.pendingPrecioRecalc = true;
        return;
      }
      this.isUpdatingPrices = true;
      this.pendingPrecioRecalc = false;
      try {
        if ((this.itemsCarrito || []).length > 0) {
          await this.calcularOfertasAplicables();
          const itemsParaCalculo = this.itemsCarrito.map((item) => {
            const p = item.producto || item.product;
            return {
              producto: p,
              cantidad: Number(item.cantidad) || 0,
              precioBase:
                p && p.precio != null
                  ? Number(p.precio)
                  : p && p.precioVenta != null
                    ? Number(p.precioVenta)
                    : 0,
            };
          });
          const resultados = aplicarOfertasACarritoPreview(
            itemsParaCalculo,
            this.ofertasAplicables || []
          );
          for (let i = 0; i < this.itemsCarrito.length; i++) {
            const res = resultados[i] || { ofertaAplicada: null };
            this.$set(this.itemsCarrito[i], "ofertaAplicada", res.ofertaAplicada || null);
          }
        }
      } finally {
        this.$nextTick(() => {
          this.isUpdatingPrices = false;
          if (this.pendingPrecioRecalc) {
            this.pendingPrecioRecalc = false;
            this.updatePrecioTotal();
          }
        });
      }
    },
    async calcularOfertasAplicables() {
      if (!this.itemsCarrito || this.itemsCarrito.length === 0) {
        this.ofertasAplicables = [];
        return;
      }
      try {
        this.loadingOfertas = true;
        const depositoId = this.depositoId || UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          this.ofertasAplicables = [];
          return;
        }
        const itemsCarritoInput = this.itemsCarrito.map((item) => {
          const p = item.producto || item.product;
          return {
            producto: (p && (p.id || p._id)) || null,
            cantidad: Number(item.cantidad) || 0,
            precioBase: (p && (p.precio != null ? p.precio : p.precioVenta)) || 0,
          };
        });
        const fecha = new Date().toISOString();
        // Presupuesto no tiene medio de pago elegido; usamos "Efectivo" para previsualizar ofertas (igual que backend al guardar)
        const medioDePagoPresupuesto = "Efectivo";
        const response = await OfertaProvider.getOfertasAplicables(
          itemsCarritoInput,
          depositoId,
          medioDePagoPresupuesto,
          fecha
        );
        if (response && response.data && response.data.ofertasAplicablesMovimiento) {
          this.ofertasAplicables = response.data.ofertasAplicablesMovimiento;
        } else {
          this.ofertasAplicables = [];
        }
      } catch (e) {
        this.ofertasAplicables = [];
      } finally {
        this.loadingOfertas = false;
      }
    },
    resetForm() {},
    tableDialogClosed() {
      this.$nextTick(() => {});
    },
    getPayload() {
      const itemsWithQty = (this.itemsCarrito || []).filter(
        (row) => Number(row.cantidad) >= 1
      );
      return {
        tipoDocumento: this.form.tipoDocumento,
        clienteId: this.form.clienteId,
        vendedorId: this.form.vendedorId || null,
        descuento: Number(this.descuento) || 0,
        recargo: this.recargoNum,
        listaPreciosVentaId: this.form.listaPreciosVentaId || null,
        items: itemsWithQty.map((row) => {
          const p = row.producto || row.product;
          const cantidad = Number(row.cantidad) || 1;
          const precioBase = (p && p.precio != null) ? Number(p.precio) : (p && p.precioVenta != null ? Number(p.precioVenta) : 0);
          const bruto = cantidad * precioBase;
          const descOferta = (row.ofertaAplicada && row.ofertaAplicada.descuento) || 0;
          const totalConOferta = bruto - descOferta;
          const precioUnitario = cantidad > 0 ? Math.round((totalConOferta / cantidad) * 100) / 100 : precioBase;
          const precioTotal = Math.round(totalConOferta * 100) / 100;
          const catalogo = row.precioCatalogo != null ? Number(row.precioCatalogo) : null;
          const precioManual =
            !!row.precioManual ||
            (catalogo != null && Number.isFinite(catalogo) && Math.abs(precioBase - catalogo) > 0.005);
          return {
            product: p,
            producto: p,
            cantidad,
            precioUnitario,
            precioTotal,
            precioBase,
            precioManual,
          };
        }),
      };
    },
    valid() {
      if (this.$refs.formRef) {
        this.$refs.formRef.validate();
      }
      if (!this.form.clienteId || this.itemsCarrito.length === 0) return false;
      const conCantidad = this.itemsCarrito.filter((row) => Number(row.cantidad) >= 1);
      if (conCantidad.length === 0) return false;
      if (this.form.tipoDocumento === "pedido") {
        const invalido = this.itemsCarrito.some((row) => {
          const stock = this.getStockRow(row);
          const cantidad = Number(row.cantidad) || 0;
          return cantidad > stock;
        });
        if (invalido) return false;
      }
      return true;
    },
    /** Para pedido: true si algún ítem tiene cantidad mayor al stock (para mostrar error). */
    pedidoExcedeStock() {
      if (this.form.tipoDocumento !== "pedido") return false;
      return this.itemsCarrito.some((row) => {
        const stock = this.getStockRow(row);
        const cantidad = Number(row.cantidad) || 0;
        return cantidad > stock;
      });
    },
  },
};
</script>

<style scoped>
.presupuesto-pedido-form-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.order-header-panel {
  flex-shrink: 0;
  background: #f4f6fb;
  border-radius: 8px;
  padding: 10px 14px;
  border-left: 4px solid #1976d2;
  box-shadow: 0 1px 3px rgba(25, 118, 210, 0.08);
}
.order-header-fields {
  display: grid;
  grid-template-columns:
    minmax(150px, 0.9fr)
    minmax(220px, 1.55fr)
    minmax(170px, 1.05fr)
    minmax(130px, 0.72fr)
    minmax(175px, 1.05fr)
    minmax(215px, 1.25fr);
  gap: 8px;
  align-items: center;
}
.order-header-panel--fixed-document .order-header-fields {
  grid-template-columns:
    minmax(260px, 1.9fr)
    minmax(175px, 1.05fr)
    minmax(130px, 0.72fr)
    minmax(190px, 1.05fr)
    minmax(260px, 1.45fr);
}
.order-header-panel--without-lista .order-header-fields {
  grid-template-columns:
    minmax(150px, 0.9fr)
    minmax(260px, 1.85fr)
    minmax(190px, 1.15fr)
    minmax(130px, 0.72fr)
    minmax(260px, 1.45fr);
}
.order-header-panel--fixed-document.order-header-panel--without-lista .order-header-fields {
  grid-template-columns:
    minmax(280px, 2fr)
    minmax(190px, 1.15fr)
    minmax(130px, 0.72fr)
    minmax(270px, 1.5fr);
}
.order-header-field {
  min-width: 0;
}
.order-header-field--recargo {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
  align-items: center;
}
.order-header-field--recargo-manual {
  grid-template-columns: minmax(0, 1fr) 96px;
}
.order-header-field--recargo-manual .v-input {
  margin-top: 0 !important;
}
.tipo-doc-radios {
  padding-left: 2px;
}

@media (max-width: 1260px) {
  .order-header-fields,
  .order-header-panel--fixed-document .order-header-fields {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 700px) {
  .order-header-fields,
  .order-header-panel--fixed-document .order-header-fields {
    grid-template-columns: 1fr;
  }
  .order-header-field--recargo-manual {
    grid-template-columns: minmax(0, 1fr) 96px;
  }
}
</style>
